<!-- Main container following application layout patterns -->
<div class="main-content">
  <!-- Header section consistent with other pages -->
  <div class="header-section">
    <div class="d-flex justify-content-between align-items-center">
      <div>
        <h1 class="main-title">{{ 'ROUTING.TITLE' | translate }}</h1>
        <p class="main-subtitle">{{ 'ROUTING.SUBTITLE' | translate }}</p>
      </div>
      
      <!-- Header actions -->
      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary" (click)="refreshRoutes()" [disabled]="isLoading">
          <i class="fas fa-sync-alt" [class.fa-spin]="isLoading"></i>
          {{ 'ROUTING.ACTIONS.REFRESH' | translate }}
        </button>
        <button class="btn btn-primary" (click)="createNewRoute()">
          <i class="fas fa-plus"></i>
          {{ 'ROUTING.ACTIONS.CREATE_NEW' | translate }}
        </button>
      </div>
    </div>

    <!-- Loading indicator -->
    <div class="text-center mt-3" *ngIf="isLoading">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'COMMON.LOADING' | translate }}</span>
      </div>
    </div>
  </div>

  <!-- Main Grid Layout: Full Width Stacked -->
  <div class="row g-4">
    
    <!-- Routes List - Full Width -->
    <div class="col-12">
      <div class="card">
        <div class="card-header">
          <div class="d-flex justify-content-between align-items-center">
            <!-- Titolo e ricerca a sinistra -->
            <div class="d-flex align-items-center gap-3">
              <div class="header-title">
                <i class="fas fa-list header-icon"></i>
                <h3>{{ 'ROUTING.ROUTES_LIST' | translate }}</h3>
                <span class="badge bg-secondary ms-2">{{ filteredRouteGroups.length }}</span>
              </div>

              <!-- Input di ricerca -->
              <div class="search-container">
                <div class="custom-search-wrapper">
                  <i class="fas fa-search search-icon"></i>
                  <input type="text" class="custom-search-input" 
                         [(ngModel)]="searchTerm"
                         (input)="filterRoutes()"
                         [placeholder]="'ROUTING.SEARCH_PLACEHOLDER' | translate"
                         [title]="'ROUTING.SEARCH_ROUTES' | translate">
                  <button *ngIf="searchTerm" class="search-clear-btn" type="button" 
                          (click)="clearSearch()"
                          [title]="'COMMON.CLEAR_SEARCH' | translate">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card-body">
          <!-- Routes list - Horizontal scroll -->
          <div class="routes-scroll-container" *ngIf="!isLoading && filteredRouteGroups.length > 0">
            <div class="routes-list">
              <div *ngFor="let routeGroup of filteredRouteGroups; trackBy: trackByRoute" 
                   class="route-item"
                   [class.route-selected]="selectedRouteGroup?.routing_code === routeGroup.routing_code"
                   (click)="selectRouteGroup(routeGroup)">
              
              <div class="route-item-header">
                <div class="route-name">
                  <h5 class="mb-1" style="font-weight: bold; color: #555;">{{ routeGroup.name }}</h5>
                  <p class="text-muted mb-0 small" *ngIf="routeGroup.description">
                    {{ routeGroup.description }}
                  </p>
                </div>
              </div>
              
              <div class="route-item-details">
                <div class="route-stats">
                  <span class="stat-item">
                    <i class="fas fa-route"></i>
                    {{ formatDistance(routeGroup.totalDistance) }}
                  </span>
                  <span class="stat-item">
                    <i class="fas fa-clock"></i>
                    {{ formatTime(routeGroup.totalTime) }}
                  </span>
                  <span class="stat-item">
                    <i class="fas fa-euro-sign"></i>
                    €{{ routeGroup.totalCost }}
                  </span>
                  <span class="stat-item">
                    <i class="fas fa-map-marker-alt"></i>
                    {{ routeGroup.legs.length }} {{ routeGroup.legs.length === 1 ? 'tappa' : 'tappe' }}
                  </span>
                </div>
                
                <div class="route-actions mt-2">
                  <div class="minimal-actions">
                    <button class="minimal-btn" 
                            (click)="selectRouteGroup(routeGroup); $event.stopPropagation()" 
                            title="{{ 'ROUTING.ACTIONS.VIEW_DETAILS' | translate }}">
                      <i class="fas fa-eye"></i>
                    </button>
                    <button class="minimal-btn" 
                            (click)="editRouteGroup(routeGroup); $event.stopPropagation()" 
                            title="{{ 'ROUTING.ACTIONS.EDIT' | translate }}">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button class="minimal-btn delete-btn" 
                            (click)="deleteRouteGroup(routeGroup); $event.stopPropagation()" 
                            title="{{ 'ROUTING.ACTIONS.DELETE' | translate }}">
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
              </div>
            </div>
          </div>

          <!-- Empty state -->
          <div *ngIf="!isLoading && filteredRouteGroups.length === 0" class="text-center py-5">
            <div class="empty-state">
              <i class="fas fa-route fa-3x text-muted mb-3"></i>
              <h4>{{ 'ROUTING.EMPTY.TITLE' | translate }}</h4>
              <p class="text-muted">{{ 'ROUTING.EMPTY.MESSAGE' | translate }}</p>
              <button class="btn btn-primary" (click)="createNewRoute()">
                <i class="fas fa-plus"></i>
                {{ 'ROUTING.ACTIONS.CREATE_FIRST' | translate }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Map - Full Width -->
    <div class="col-12">
      <div class="card">
        <div class="card-header">
          <div class="d-flex justify-content-between align-items-center">
            <div class="header-title">
              <i class="fas fa-map header-icon"></i>
              <h3>{{ selectedRouteGroup ? selectedRouteGroup.name : ('ROUTING.MAP.TITLE' | translate) }}</h3>
            </div>
            <div class="d-flex gap-2" *ngIf="selectedRouteGroup">
              <button class="btn btn-outline-secondary btn-sm" 
                      (click)="toggleWarehouses()" 
                      [class.btn-success]="showingWarehouses">
                <i class="fas fa-warehouse"></i>
                {{ showingWarehouses ? ('ROUTING.MAP.HIDE_WAREHOUSES' | translate) : ('ROUTING.MAP.SHOW_WAREHOUSES' | translate) }}
              </button>
              <button class="btn btn-outline-secondary btn-sm" (click)="clearMap()">
                <i class="fas fa-eraser"></i>
                {{ 'ROUTING.MAP.CLEAR' | translate }}
              </button>
            </div>
          </div>
        </div>
        <div class="card-body p-0">
          <div id="map" class="map-container-grid"></div>
          
          <!-- No route selected state -->
          <div *ngIf="!selectedRouteGroup && !isLoading" class="map-empty-state">
            <div class="empty-state">
              <i class="fas fa-map fa-3x text-muted mb-3"></i>
              <h4>{{ 'ROUTING.MAP.NO_ROUTE_SELECTED' | translate }}</h4>
              <p class="text-muted">{{ 'ROUTING.MAP.SELECT_ROUTE_MESSAGE' | translate }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- General Info - Full Width -->
    <div class="col-12" *ngIf="selectedRouteGroup">
      <div class="card">
        <div class="card-header">
          <div class="header-title">
            <i class="fas fa-info-circle header-icon"></i>
            <h4>{{ 'ROUTING.DETAILS.GENERAL' | translate }}</h4>
          </div>
        </div>
        <div class="card-body">
          <div class="info-grid">
            <div class="info-item">
              <label>{{ 'ROUTING.DETAILS.STATUS' | translate }}</label>
              <span class="badge" [class]="getStatusClass(selectedRouteGroup.status)">
                {{ getStatusLabel(selectedRouteGroup.status) }}
              </span>
            </div>
            <div class="info-item">
              <label>{{ 'ROUTING.DETAILS.DISTANCE' | translate }}</label>
              <span class="fw-bold">{{ formatDistance(selectedRouteGroup.totalDistance) }}</span>
            </div>
            <div class="info-item">
              <label>{{ 'ROUTING.DETAILS.TIME' | translate }}</label>
              <span class="fw-bold">{{ formatTime(selectedRouteGroup.totalTime) }}</span>
            </div>
            <div class="info-item">
              <label>{{ 'ROUTING.DETAILS.COST' | translate }}</label>
              <span class="fw-bold text-success">€{{ selectedRouteGroup.totalCost }}</span>
            </div>
            <div class="info-item" *ngIf="selectedRouteGroup.transport_mode">
              <label>{{ 'ROUTING.DETAILS.TRANSPORT_MODE' | translate }}</label>
              <span>{{ selectedRouteGroup.transport_mode }}</span>
            </div>
            <div class="info-item" *ngIf="selectedRouteGroup.vehicle_type">
              <label>{{ 'ROUTING.DETAILS.VEHICLE_TYPE' | translate }}</label>
              <span>{{ selectedRouteGroup.vehicle_type }}</span>
            </div>
            <div class="info-item" *ngIf="selectedRouteGroup.carrier_code">
              <label>{{ 'ROUTING.DETAILS.CARRIER' | translate }}</label>
              <span>{{ selectedRouteGroup.carrier_code }}</span>
            </div>
            <div class="info-item">
              <label>{{ 'ROUTING.DETAILS.CREATED' | translate }}</label>
              <span>{{ selectedRouteGroup.legs[0].routing_created_at | date:'dd/MM/yyyy' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tappe del Percorso - Full Width -->
    <div class="col-12" *ngIf="selectedRouteGroup">
      <div class="card">
        <div class="card-header">
          <div class="d-flex justify-content-between align-items-center">
            <div class="header-title">
              <i class="fas fa-route header-icon"></i>
              <h4>Tappe del Percorso</h4>
              <span class="badge bg-secondary ms-2">{{ selectedRouteGroup.legs.length + 1 }}</span>
            </div>
            <button class="btn btn-primary btn-sm" (click)="addNewLeg()">
              <i class="fas fa-plus"></i>
              Aggiungi Tappa
            </button>
          </div>
        </div>
        <div class="card-body">
          <div class="timeline-container">
            <!-- Prima tappa (Partenza) -->
            <div class="timeline-stop">
              <div class="timeline-marker">
                <div class="marker-circle start">
                  <i class="fas fa-flag"></i>
                </div>
              </div>
              <div class="timeline-content">
                <div class="stop-header">
                  <div class="stop-title">
                    {{ getLocationName(selectedRouteGroup.legs[0].routing_location_start_code) }}
                    <span class="stop-type">{{ selectedRouteGroup.legs[0].routing_location_start_type }}</span>
                  </div>
                  <button class="btn-edit" (click)="editStop(0, 'start')" title="Modifica">
                    <i class="fas fa-edit"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Legs con trasportatori -->
            <ng-container *ngFor="let leg of selectedRouteGroup.legs; let i = index">
              <!-- Linea di collegamento con info trasportatore -->
              <div class="timeline-leg">
                <div class="timeline-line"></div>
                <div class="leg-transport-info">
                  <div class="transport-card">
                    <div class="transport-header" (click)="toggleLegDetails(i)">
                      <div class="transport-title">
                        <i class="fas fa-truck"></i>
                        <span class="transport-label">Leg {{ i + 1 }}</span>
                        <span class="transport-summary" *ngIf="leg.routing_carrier_code">
                          - {{ leg.routing_carrier_code }}
                        </span>
                      </div>
                      <i class="fas" [ngClass]="legDetailsExpanded[i] ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                    </div>
                    
                    <div class="transport-details" *ngIf="legDetailsExpanded[i]">
                      <div class="transport-row" *ngIf="leg.routing_carrier_code">
                        <span class="detail-label">Trasportatore:</span>
                        <span>{{ leg.routing_carrier_code }}</span>
                      </div>
                      <div class="transport-row" *ngIf="leg.routing_vehicle_type">
                        <span class="detail-label">Veicolo:</span>
                        <span>{{ leg.routing_vehicle_type }}</span>
                      </div>
                      <div class="transport-row" *ngIf="leg.routing_distance_km">
                        <span class="detail-label">Distanza:</span>
                        <span>{{ formatDistance(leg.routing_distance_km) }}</span>
                      </div>
                      <div class="transport-row" *ngIf="leg.routing_estimated_time">
                        <span class="detail-label">Tempo:</span>
                        <span>{{ formatTime(leg.routing_estimated_time) }}</span>
                      </div>
                      <div class="transport-row" *ngIf="leg.routing_cost">
                        <span class="detail-label">Costo:</span>
                        <span>€{{ leg.routing_cost }}</span>
                      </div>
                      
                      <div class="transport-actions">
                        <button class="btn-action" (click)="editLeg(i); $event.stopPropagation()" title="Modifica">
                          <i class="fas fa-edit"></i>
                        </button>
                        <button class="btn-action btn-delete" (click)="deleteLeg(i); $event.stopPropagation()" title="Elimina">
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tappa successiva -->
              <div class="timeline-stop">
                <div class="timeline-marker">
                  <div class="marker-circle" [class.end]="i === selectedRouteGroup.legs.length - 1">
                    <span class="marker-number">{{ i + 2 }}</span>
                  </div>
                </div>
                <div class="timeline-content">
                  <div class="stop-header">
                    <div class="stop-title">
                      {{ getLocationName(leg.routing_location_end_code) }}
                      <span class="stop-type">{{ leg.routing_location_end_type }}</span>
                    </div>
                    <div class="stop-actions">
                      <button class="btn-edit" (click)="editStop(i, 'end')" title="Modifica">
                        <i class="fas fa-edit"></i>
                      </button>
                      <button class="btn-delete" 
                              *ngIf="i === selectedRouteGroup.legs.length - 1"
                              (click)="deleteStop(i)" 
                              title="Elimina">
                        <i class="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </ng-container>

            <!-- Aggiungi nuova tappa -->
            <div class="timeline-add-stop" *ngIf="selectedRouteGroup.legs.length > 0">
              <button class="btn btn-outline-primary w-100" (click)="addNewLeg()">
                <i class="fas fa-plus-circle me-2"></i>
                Aggiungi Nuova Tappa
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
